import React from 'react';
import { Panel, Section } from './primitives';

interface TypeFacts {
  name: string;
  forWhom: string;
  rows: { label: string; value: string }[];
}

// Same rows in the same order on both sides, so the two read across.
const TYPES: TypeFacts[] = [
  {
    name: 'Sole proprietor',
    forWhom: 'For an individual agent without an EIN.',
    rows: [
      { label: "You'll need", value: 'Your legal name, the last 4 digits of your SSN, your home address, a mobile number, and 2 sample texts' },
      { label: 'Numbers that can text', value: 'One' },
      { label: 'Daily limit', value: 'About 1,000 texts, varies by carrier' },
      { label: "Confirming it's you", value: 'A text to your mobile phone' },
    ],
  },
  {
    name: 'Low Volume Standard',
    forWhom: 'For a business or agency with an EIN.',
    rows: [
      {
        label: "You'll need",
        value: 'Business name and address, an EIN at least 45 days old, the IRS CP 575 letter, and 2 sample texts',
      },
      { label: 'Numbers that can text', value: 'All of them, including numbers you buy later' },
      { label: 'Daily limit', value: 'Up to 6,000 message segments' },
      { label: "Confirming it's you", value: 'Our team reviews your CP 575 letter' },
    ],
  },
];

export function RegistrationGuide({
  canRegisterForAgency,
  agencyName,
}: {
  canRegisterForAgency: boolean;
  agencyName?: string | null;
}) {
  return (
    <>
      <Section
        id="a2p-types"
        title="Which registration fits you"
        description="You pick one in the first step."
      >
        <Panel className="overflow-hidden">
          <div className="grid divide-y divide-neutral-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            {TYPES.map((t) => (
              <div key={t.name} className="px-4 py-5 sm:px-6">
                <h4 className="!text-[15px] font-semibold text-neutral-900">{t.name}</h4>
                <p className="mt-0.5 text-sm text-neutral-500">{t.forWhom}</p>
                <dl className="mt-4 space-y-3.5">
                  {t.rows.map((r) => (
                    <div key={r.label}>
                      <dt className="text-xs text-neutral-500">{r.label}</dt>
                      <dd className="mt-0.5 text-pretty text-sm leading-relaxed text-neutral-900">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          <p className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-3 text-sm text-neutral-600 sm:px-6">
            Starting as a sole proprietor? You can move to Low Volume Standard later through support.
          </p>
        </Panel>
      </Section>

      {canRegisterForAgency && (
        <Section
          id="a2p-agency"
          title={`Registering for ${agencyName || 'your agency'}?`}
          description={
            <>
              Choose Low Volume Standard, then tick <span className="font-medium text-neutral-900">Register once</span>.
              One registration covers every agent below you, including sub-agencies, and the numbers they buy. The
              daily limit is shared by everyone it covers.
            </>
          }
        />
      )}
    </>
  );
}
